<template>
  <span
    :class="[
      'avatar',
      sizeClass,
      shapeClass,
      'dq-avatar-shell',
      fallbackToneClass,
    ]"
  >
    <img
      v-if="showImage"
      :src="resolvedSrc"
      :alt="altText"
      :class="imgClass"
      @error="handleImageError"
    />
    <span
      v-else
      :class="['dq-avatar-fallback', shapeClass]"
      :title="altText"
      aria-hidden="true"
    >
      {{ initials }}
    </span>
  </span>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { resolveAvatarUrl } from "@/utils/imageHelpers";

const props = defineProps({
  src: { type: String, default: "" },
  name: { type: String, default: "" },
  alt: { type: String, default: "" },
  sizeClass: { type: String, default: "avatar-md" },
  shapeClass: { type: String, default: "rounded-circle" },
  imgClass: { type: String, default: "img-fluid" },
});

const imageFailed = ref(false);

watch(
  () => props.src,
  () => {
    imageFailed.value = false;
  },
);

const normalizedSrc = computed(() => String(props.src || "").trim());

const isFilenamePlaceholder = computed(() => {
  const src = normalizedSrc.value;

  return !!src && !src.includes("/") && !src.startsWith("http") && !src.startsWith("data:");
});

const resolvedSrc = computed(() => {
  if (!normalizedSrc.value || isFilenamePlaceholder.value) {
    return "";
  }

  return resolveAvatarUrl(normalizedSrc.value);
});

const showImage = computed(() => !!resolvedSrc.value && !imageFailed.value);

const altText = computed(() => props.alt || props.name || "Avatar");

const initials = computed(() => {
  const parts = String(props.name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) {
    return "?";
  }

  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
});

const fallbackToneClass = computed(() => {
  const tones = [
    "dq-tone-1",
    "dq-tone-2",
    "dq-tone-3",
    "dq-tone-4",
    "dq-tone-5",
    "dq-tone-6",
  ];

  const seed = Array.from(String(props.name || "avatar")).reduce(
    (sum, char) => sum + char.charCodeAt(0),
    0,
  );

  return tones[seed % tones.length];
});

function handleImageError() {
  imageFailed.value = true;
}
</script>

<style scoped>
.dq-avatar-shell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.dq-avatar-shell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dq-avatar-fallback {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
}

.dq-tone-1 .dq-avatar-fallback {
  background: linear-gradient(135deg, #e0f2fe, #bfdbfe);
  color: #1d4ed8;
}

.dq-tone-2 .dq-avatar-fallback {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  color: #166534;
}

.dq-tone-3 .dq-avatar-fallback {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
}

.dq-tone-4 .dq-avatar-fallback {
  background: linear-gradient(135deg, #fce7f3, #fbcfe8);
  color: #9d174d;
}

.dq-tone-5 .dq-avatar-fallback {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #6d28d9;
}

.dq-tone-6 .dq-avatar-fallback {
  background: linear-gradient(135deg, #cffafe, #a5f3fc);
  color: #155e75;
}
</style>
